import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { LuMessageSquarePlus, LuCheck, LuSend } from "react-icons/lu";
import { Dialog, Btn, Note } from "./Ui";
import "./feedbackWidget.css";

/* AI-first feedback: a short conversation instead of a form. feedback_chat_v2
   figures out bug / feature / general feedback and gathers the details; when it
   wraps up we file it via the existing all_user_query_forms_v2 (store + team
   notification to marketing@4spheresolutions.com). */
const FEEDBACK_CHAT_API = "https://us-east1-foursssolutions.cloudfunctions.net/feedback_chat_v2";
const STORE_API = "https://us-east1-foursssolutions.cloudfunctions.net/all_user_query_forms_v2";

export default function FeedbackWidget() {
  const email = useSelector((s) => s.auth?.auth?.email) || "";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const logRef = useRef(null);
  const startedRef = useRef(false);

  const callChat = async (m) => {
    const res = await fetch(FEEDBACK_CHAT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: m, email }),
    });
    return res.json();
  };

  const start = async () => {
    setBusy(true); setError("");
    try {
      const data = await callChat([]);
      setMsgs(data.message ? [{ role: "assistant", content: data.message }] : []);
    } catch (err) {
      console.error("Feedback chat start failed:", err);
      setError("We couldn't start the chat.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && !startedRef.current) { startedRef.current = true; start(); }
    if (!open) startedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [msgs, busy]);

  const storeFeedback = async (requestType, feedbackText) => {
    try {
      await fetch(STORE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email || "anonymous",
          contact_number: "",
          request_type: requestType,
          feedback_text: feedbackText,
          app_name: "careersavvy",
        }),
      });
    } catch (err) {
      console.error("Store feedback failed:", err);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const where = typeof window !== "undefined" ? window.location.pathname : "";
    const newMsgs = [...msgs, { role: "user", content: text }];
    setMsgs(newMsgs); setInput(""); setBusy(true); setError("");
    try {
      const data = await callChat(newMsgs);
      if (data.message) setMsgs((m) => [...m, { role: "assistant", content: data.message }]);
      if (data.done) {
        const summary = data.feedback_text || newMsgs.filter((x) => x.role === "user").map((x) => x.content).join("\n");
        await storeFeedback(data.request_type || "General Feedback", where ? `${summary}\n\n[page: ${where}]` : summary);
        setSent(true);
      }
    } catch (err) {
      console.error("Feedback chat failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setMsgs([]); setInput(""); setSent(false); setError(""); }, 200);
  };

  return (
    <>
      <button type="button" className="fbw-fab" onClick={() => setOpen(true)} aria-label="Send feedback" title="Send feedback">
        <LuMessageSquarePlus size={18} />
        <span className="fbw-fab-text">Feedback</span>
      </button>

      <Dialog open={open} onClose={close}>
        {sent ? (
          <div className="fbw-done stack-3">
            <span className="fbw-done-ic"><LuCheck size={24} /></span>
            <div className="stack-2">
              <h3 className="t-h3">Thank you!</h3>
              <p className="t-sm">Your feedback is on its way to our team — we read every message.</p>
            </div>
            <div><Btn variant="quiet" onClick={close}>Close</Btn></div>
          </div>
        ) : (
          <div className="fbw-chat stack-3">
            <div className="stack-2">
              <h3 className="t-h3">Share your feedback</h3>
              <p className="t-sm">Chat with us — a bug, an idea, anything. Just say what's on your mind.</p>
            </div>
            <div className="fbw-log" ref={logRef}>
              {msgs.map((m, i) => (
                <div key={i} className={`fbw-msg ${m.role}`}>{m.content}</div>
              ))}
              {busy ? <div className="fbw-msg assistant fbw-typing"><span /><span /><span /></div> : null}
              {error ? (
                <Note tone="danger"><span>{error} <button type="button" className="fbw-link" onClick={start}>Retry</button></span></Note>
              ) : null}
            </div>
            <div className="fbw-input">
              <input
                className="input"
                value={input}
                placeholder="Type your message…"
                disabled={busy}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
              />
              <Btn className="btn-mint btn-pill fbw-send" onClick={send} disabled={busy || !input.trim()} aria-label="Send">
                <LuSend size={16} />
              </Btn>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
